/**
 * How money is paid, and what money is counted in.
 *
 * TWO settings share this tab because they answer the same question from two
 * sides: the currency is the unit every number on every screen is in, and the
 * payment methods are how that money left the house. Neither is big enough for
 * a tab of its own, and splitting them would mean a third place to look for
 * "where do I change the money".
 *
 * PAYMENT METHODS are a flat list of names. Card, Cash, PayPal. Add one,
 * rename one, archive one. That is the entire feature, and it is deliberately
 * not more: an earlier version also asked what KIND each one was, so that two
 * cards could group together in a filter. Nobody wanted that grouping, and it
 * charged a question on every row to provide it. Deleting is offered only
 * while nothing has been paid with it. After that the honest operation is
 * archiving: it leaves the picker, and every past entry keeps saying what it
 * actually said.
 *
 * THE CURRENCY RELABELS, it does not convert — there is nothing to convert
 * from, because an amount is stored as a count of minor units and no row
 * anywhere records which currency it was counted in. 47.32 stays 47.32 and
 * starts reading as dollars. The screen says that in those words rather than
 * in a warning triangle, and asks once before doing it to a ledger that has
 * entries in it. See `members.service.ts` for the one change that is refused.
 */
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type { Household, PaymentMeans } from '@et/contracts';
import { api, messageOf, type Identity } from './api.js';
import { currencyChoices } from './currencies.js';

interface Props {
  identity: Identity;
  means: PaymentMeans[];
  household: Household;
  canEdit: boolean;
  onChanged: (next: PaymentMeans[]) => void;
  onHouseholdChanged: (next: Household) => void;
}

export function PaymentMethods({
  identity,
  means,
  household,
  canEdit,
  onChanged,
  onHouseholdChanged,
}: Props) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(work: () => Promise<PaymentMeans[]>) {
    setBusy(true);
    setError(null);
    try {
      onChanged(await work());
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  async function add(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    await run(async () => {
      const next = await api.createPaymentMeans(identity, { name: name.trim() });
      setName('');
      return next;
    });
  }

  const live = means.filter((m) => !m.archived);
  const archived = means.filter((m) => m.archived);

  return (
    <section className="settings">
      <CurrencySetting
        identity={identity}
        household={household}
        canEdit={canEdit}
        onChanged={onHouseholdChanged}
      />

      <div className="settings-block">
        <h2>Payment methods</h2>
        {error && <p className="error">{error}</p>}

        <ul className="means-list">
          {live.map((m) => (
            <li key={m.id} className="means-row">
              <input
                className="means-name"
                defaultValue={m.name}
                disabled={!canEdit || busy}
                aria-label={`Name of ${m.name}`}
                onBlur={(e) => {
                  const next = e.target.value.trim();
                  if (next && next !== m.name)
                    void run(() => api.updatePaymentMeans(identity, m.id, { name: next }));
                }}
              />

              <span className="means-uses">{m.useCount > 0 ? `${m.useCount}×` : <em>unused</em>}</span>

              {canEdit && (
                <span className="means-actions">
                  <button
                    type="button"
                    className="chip"
                    disabled={busy}
                    onClick={() => void run(() => api.updatePaymentMeans(identity, m.id, { archived: true }))}
                  >
                    Archive
                  </button>
                  {/* Only while it is safe. Once something has been paid with
                      it, deleting would quietly rewrite what those entries say. */}
                  {m.useCount === 0 && (
                    <button
                      type="button"
                      className="chip"
                      disabled={busy}
                      onClick={() => void run(() => api.deletePaymentMeans(identity, m.id))}
                    >
                      Delete
                    </button>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>

        {canEdit && (
          <form className="means-add" onSubmit={add}>
            <input
              placeholder="Add one — “PayPal”, “Joint account”"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label="New payment method"
            />
            <button type="submit" className="primary compact" disabled={!name.trim() || busy}>
              Add
            </button>
          </form>
        )}

        {archived.length > 0 && (
          <details className="means-archived">
            <summary>{archived.length} archived — still on past entries, no longer offered</summary>
            <ul className="means-list">
              {archived.map((m) => (
                <li key={m.id} className="means-row means-row-off">
                  <span className="means-name-static">{m.name}</span>
                  <span className="means-uses">{m.useCount > 0 ? `${m.useCount}×` : <em>unused</em>}</span>
                  {canEdit && (
                    <button
                      type="button"
                      className="chip"
                      disabled={busy}
                      onClick={() => void run(() => api.updatePaymentMeans(identity, m.id, { archived: false }))}
                    >
                      Restore
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </section>
  );
}

/**
 * The household's currency.
 *
 * A `<select>` and two sentences, because the sentences are the feature. The
 * mechanics are trivial — one column, one PATCH — and everything that matters
 * about this control is whether the person understands, before they press it,
 * that their 47.32 will still say 47.32 afterwards.
 *
 * So: the consequence is stated in the ordinary text under the picker, not
 * hidden behind a warning icon, and choosing a new code does NOT save. It
 * stages, and a second, specific press ("Switch 41 entries to USD") is what
 * commits. An empty ledger skips that step — there is nothing to relabel, and
 * a confirmation about no entries is the kind of ceremony that teaches people
 * to click through the ones that count.
 */
function CurrencySetting({
  identity,
  household,
  canEdit,
  onChanged,
}: {
  identity: Identity;
  household: Household;
  canEdit: boolean;
  onChanged: (next: Household) => void;
}) {
  const current = household.baseCurrency;
  const entries = household.recordedEntries;

  /** Staged, not saved. Null means the picker is showing what is in force. */
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const choices = useMemo(() => currencyChoices(current, entries), [current, entries]);
  /** The decimal filter is on exactly when there is a ledger to protect. */
  const narrowed = entries > 0;

  async function commit(code: string) {
    setBusy(true);
    setError(null);
    try {
      onChanged(await api.updateHousehold(identity, { baseCurrency: code }));
      setPicked(null);
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  function choose(code: string) {
    if (code === current) {
      setPicked(null);
      return;
    }
    // Nothing recorded yet means nothing to relabel: save it and move on.
    if (entries === 0) {
      void commit(code);
      return;
    }
    setPicked(code);
    setError(null);
  }

  if (!canEdit) {
    return (
      <div className="settings-block">
        <h2>Currency</h2>
        <p className="hint">
          Everything in this household is counted in <strong>{current}</strong>. Only an owner can
          change it.
        </p>
      </div>
    );
  }

  return (
    <div className="settings-block">
      <h2>Currency</h2>

      <div className="currency-row">
        <select
          className="currency-select"
          value={picked ?? current}
          disabled={busy}
          aria-label="Household currency"
          onChange={(e) => choose(e.target.value)}
        >
          {choices.map((option) => (
            <option key={option.code} value={option.code}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <p className="hint">
        Every amount on every screen is in this. Changing it <strong>renames the unit and
        leaves the numbers alone</strong> — a 47.32 shop stays 47.32 and starts reading as the new
        currency. Nothing is converted, and nothing is lost: switch back and you are exactly where
        you started.
      </p>

      {/* Said before the attempt, not after a refusal. The API enforces this
          too — this is the half that lets someone plan around it. */}
      {narrowed && (
        <p className="hint">
          With {entries} {entries === 1 ? 'entry' : 'entries'} on file the list is limited to
          currencies written the same way as {current}. An amount is stored as a count of the
          smallest unit with no record of its currency, so moving to one with a different number of
          decimal places — {current} to JPY, say — would republish your whole ledger a hundred times
          off.
        </p>
      )}

      {error && <p className="error">{error}</p>}

      {picked && picked !== current && (
        <div className="currency-confirm">
          <p>
            {entries} recorded {entries === 1 ? 'entry' : 'entries'} will be read as {picked} instead
            of {current}. The figures do not move.
          </p>
          <div className="currency-confirm-actions">
            <button
              type="button"
              className="primary compact"
              disabled={busy}
              onClick={() => void commit(picked)}
            >
              Switch to {picked}
            </button>
            <button type="button" className="chip" disabled={busy} onClick={() => setPicked(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
